/**
 * **SSE クライアント**（§9 / §2.9）。
 *
 * ## `EventSource` を使えない
 *
 * §9:「**`EventSource` は使えない（POST とカスタムヘッダ非対応）**。
 * `fetch` + `ReadableStream` の薄いクライアントを書く」。
 *
 * この API は **`POST /api/sessions` と `POST /api/turn`** で、
 * **`Authorization: Bearer <id_token>` を送る**（M15 で実装した経路）。
 * `EventSource` は GET しかできず、ヘッダも付けられない。
 *
 * ## SSE は演出、真実は DynamoDB（§9 / §4.7）
 *
 * **`done` を受け取れなくても、サーバ側のターンは完了している。**
 * **この層は「届かなかった」を隠さない** — `done` を見ずに終わったら
 * **`incomplete` として呼び出し側に返す**（呼び出し側が再取得の導線を出す）。
 *
 * ## サーバが送るもの（M15 の `apps/api/src/sse.ts` と 1 対 1）
 *
 * - `id: <version>-<接続内の連番>` — **`version` はセッションを跨いで意味を持つ**
 * - `event: <type>` / `data: <json>`
 * - **`: ping`（コメント行）** — **15 秒間隔のハートビート**。
 *   **無送信区間は M15 実測で最大 96.6 秒**あり、これが無いと
 *   ブラウザ・NAT・CloudFront が切る。**コメント行は捨てる。**
 */

export interface SseEvent {
  readonly type: string;
  readonly data: unknown;
  /** `<version>-<seq>`。**再接続時に「どこまで受け取ったか」を伝える** */
  readonly id: string | undefined;
}

export type SseOutcome =
  /** `done` を受け取って正常に終わった */
  | { readonly kind: 'complete' }
  /**
   * **`done` を見ずにストリームが終わった。**
   * **サーバ側のターンは完了している可能性がある**ので、
   * **失敗として扱わず「読み直しが要る」として返す**（§4.7）。
   */
  | { readonly kind: 'incomplete'; readonly reason: string }
  /** HTTP レベルで失敗した（401 / 400 / 500） */
  | { readonly kind: 'http_error'; readonly status: number; readonly body: string }
  /**
   * **`fetch` 自体が失敗した**（ネットワーク断 / CORS / DNS）。
   * **例外で抜けさせない** — 呼び出し側が `void` で捨てると**画面に何も出ない**。
   */
  | { readonly kind: 'network_error'; readonly message: string };

export interface SseRequest {
  readonly url: string;
  readonly idToken: string;
  readonly body: unknown;
  readonly signal?: AbortSignal;
}

/**
 * **1 本のストリームを読み切る。**
 *
 * **`onEvent` は同期で呼ぶ** — 非同期にすると
 * **イベントの順序が入れ替わりうる**（`token` の後に `state` が来る等）。
 */
export async function readSse(
  req: SseRequest,
  onEvent: (ev: SseEvent) => void,
): Promise<SseOutcome> {
  /**
   * **`fetch` 自体の失敗を投げっぱなしにしない**（M16。**実使用で沈黙した**）。
   *
   * ネットワーク断・CORS・DNS で `fetch` は **reject する**。
   * **呼び出し側が `void` で捨てていると、画面には何も出ない**
   * （**送信ボタンが回り続けて反応しないように見える**）。
   *
   * **失敗は必ず値で返す** — 例外で抜けると、
   * **握りつぶす経路が呼び出し側の数だけできる**。
   */
  let res: Response;
  try {
    res = await fetch(req.url, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${req.idToken}`,
      },
      body: JSON.stringify(req.body),
      ...(req.signal === undefined ? {} : { signal: req.signal }),
    });
  } catch (e) {
    return { kind: 'network_error', message: e instanceof Error ? e.message : '接続できなかった' };
  }

  if (!res.ok) {
    return { kind: 'http_error', status: res.status, body: await res.text().catch(() => '') };
  }
  if (res.body === null) {
    return { kind: 'incomplete', reason: 'body が無い' };
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let sawDone = false;

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      /**
       * **イベントの区切りは空行**（`\n\n`）。
       * **最後の断片は次のチャンクに繰り越す** —
       * **チャンク境界はイベント境界と一致しない**（実際に途中で切れる）。
       */
      let sep = buffer.indexOf('\n\n');
      while (sep !== -1) {
        const chunk = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const ev = parseEvent(chunk);
        if (ev !== undefined) {
          if (ev.type === 'done') sawDone = true;
          onEvent(ev);
        }
        sep = buffer.indexOf('\n\n');
      }
    }
  } catch (e) {
    /** **切断を握りつぶさない**（サーバ側は完了しているかもしれない） */
    return { kind: 'incomplete', reason: e instanceof Error ? e.message : '接続が切れた' };
  }

  return sawDone ? { kind: 'complete' } : { kind: 'incomplete', reason: 'done が届かなかった' };
}

/**
 * 1 イベント分のテキストを解釈する。
 *
 * **コメント行（`:` で始まる）は捨てる** — **ハートビート（`: ping`）**が
 * これで届く。**`event:` として送っていない**のは、
 * **クライアントが「新しい出来事が起きた」と誤読しない**ためである。
 */
export function parseEvent(chunk: string): SseEvent | undefined {
  let id: string | undefined;
  let type: string | undefined;
  const dataLines: string[] = [];

  for (const line of chunk.split('\n')) {
    if (line === '' || line.startsWith(':')) continue;
    if (line.startsWith('id: ')) id = line.slice(4);
    else if (line.startsWith('event: ')) type = line.slice(7);
    else if (line.startsWith('data: ')) dataLines.push(line.slice(6));
  }

  if (type === undefined) return undefined;

  let data: unknown = undefined;
  if (dataLines.length > 0) {
    const raw = dataLines.join('\n');
    try {
      data = JSON.parse(raw);
    } catch {
      /** **壊れた JSON を握りつぶさない** — 生の文字列として渡す */
      data = raw;
    }
  }
  return { type, data, id };
}

/**
 * **`id` から `version` を取り出す**（`<version>-<seq>`）。
 *
 * **接続内の連番ではなく `version` を使う** — 再接続した学習者は
 * **これを送って続きから受け取る**（接続内の連番は再接続で 0 に戻る）。
 */
export function versionOf(id: string | undefined): number | undefined {
  if (id === undefined) return undefined;
  const n = Number(id.split('-')[0]);
  return Number.isFinite(n) ? n : undefined;
}
